import InternalAPI from "../../../../../app/axios.js"
import { isAuthorLanguage } from "../types"
import "../types"

/**
 * @param {object} err
 * @returns {string}
 */
function normalizeError(err) {
  try {
    const desc = err.response && err.response.data && err.response.data.desc
    if (desc && desc.id) return desc.id
    const msg = err.response && err.response.data && err.response.data.message
    if (msg) return msg
  } catch (_) { /* intentional */ }
  if (err && err.message) return err.message
  return "something went wrong"
}

/**
 * @param {object} dto
 * @returns {import("../types").CorporateAuthor|{_languageError: string}|null}
 */
function mapAuthor(dto) {
  if (!dto || typeof dto !== "object") return null
  const lang = Object.prototype.hasOwnProperty.call(dto, "language") ? dto.language : undefined
  let mappedLang = null
  if (lang !== null && lang !== undefined) {
    if (!isAuthorLanguage(lang)) {
      return { _languageError: `unknown language value: ${lang}` }
    }
    mappedLang = lang
  }
  return {
    author_id: dto.author_id,
    author_name: dto.author_name,
    author_slug: dto.author_slug,
    author_desc: dto.author_desc,
    author_facebook_url: dto.author_facebook_url,
    author_x_url: dto.author_x_url,
    author_in_url: dto.author_in_url,
    author_banner: dto.author_banner,
    author_banner_mobile: dto.author_banner_mobile,
    author_avatar: dto.author_avatar,
    author_status: dto.author_status,
    created_at: dto.created_at,
    language: mappedLang,
  }
}

/**
 * HTTP/multipart/DTO-mapping boundary for Corporate Author. Implements the
 * `CorporateAuthorRepository` contract from `app/types/index.js`. Consumed
 * by `CorporateAuthorUseCase`, which owns language validation for outgoing
 * queries/forms; this class validates only inbound DTO language values.
 */
export class CorporateAuthorRepository extends InternalAPI {
  /** @param {object} [client] injected Axios-like client for testability */
  constructor(client) {
    super()
    if (client) this.$axios = client
    this.endpoint = "/v2/authors"
  }

  /**
   * @param {import("../types").CorporateAuthorListQuery} query
   * @returns {Promise<import("../types").RepositoryResult<import("../types").CorporateAuthor[]>>}
   */
  async getAll(query) {
    try {
      const res = await this.$axios.get(`${this.endpoint}/admin/data`, {
        params: {
          page: query.page,
          per_page: query.per_page,
          author_name: query.author_name,
          language: query.language,
        }
      })
      const list = res.data && res.data.data && res.data.data.data
      if (!Array.isArray(list)) {
        return { data: [], err: "unexpected response format" }
      }
      const mapped = []
      for (const dto of list) {
        const entity = mapAuthor(dto)
        if (entity && entity._languageError) {
          return { data: [], err: entity._languageError }
        }
        mapped.push(entity)
      }
      return { data: mapped, err: null }
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message
      if (msg === "record not found") return { data: [], err: null }
      return { data: [], err: normalizeError(err) }
    }
  }

  /**
   * @param {number|string} id
   * @returns {Promise<import("../types").RepositoryResult<import("../types").CorporateAuthor|null>>}
   */
  async getOne(id) {
    try {
      const res = await this.$axios.get(`${this.endpoint}/admin/${id}`)
      const dto = res.data && res.data.data
      if (!dto || typeof dto !== "object") {
        return { data: null, err: "unexpected response format" }
      }
      const entity = mapAuthor(dto)
      if (entity && entity._languageError) {
        return { data: null, err: entity._languageError }
      }
      return { data: entity, err: null }
    } catch (err) {
      return { data: null, err: normalizeError(err) }
    }
  }

  /**
   * @param {string} name
   * @param {import("../types").AuthorLanguage} [language]
   * @returns {Promise<import("../types").RepositoryResult<boolean>>}
   */
  async checkAvailable(name, language) {
    try {
      const params = { author_name: name }
      if (language !== undefined && language !== null && isAuthorLanguage(language)) {
        params.language = language
      }
      const res = await this.$axios.get(`${this.endpoint}/admin/check`, { params })
      const available = res.data && res.data.data && res.data.data.available
      if (typeof available !== "boolean") {
        return { data: false, err: "unexpected response format" }
      }
      return { data: available, err: null }
    } catch (err) {
      return { data: false, err: normalizeError(err) }
    }
  }

  /**
   * @param {import("../types").CorporateAuthorForm} form
   * @returns {Promise<import("../types").MutationResult>}
   */
  async createOne(form) {
    try {
      const fd = new FormData()
      fd.append("author_name", form.author_name)
      fd.append("author_slug", form.author_slug)
      fd.append("author_desc", form.author_desc)
      fd.append("author_facebook_url", form.author_facebook_url || "")
      fd.append("author_x_url", form.author_x_url || "")
      fd.append("author_in_url", form.author_in_url || "")
      fd.append("author_status", form.author_status)
      if (form.author_banner) fd.append("author_banner", form.author_banner)
      if (form.author_banner_mobile) fd.append("author_banner_mobile", form.author_banner_mobile)
      if (form.author_avatar) fd.append("author_avatar", form.author_avatar)
      fd.append("language", form.language)
      const res = await this.$axios.post(`${this.endpoint}/admin`, fd)
      const dto = res.data && res.data.data
      const entity = dto ? mapAuthor(dto) : null
      const safeEntity = entity && !entity._languageError ? entity : null
      return { data: safeEntity, message: "", err: null }
    } catch (err) {
      return { data: null, message: "", err: normalizeError(err) }
    }
  }

  /**
   * @param {number|string} id
   * @param {Partial<import("../types").CorporateAuthorForm>} command
   * @returns {Promise<import("../types").MutationResult>}
   */
  async updateOne(id, command) {
    try {
      const fd = new FormData()
      const supported = [
        "author_name", "author_slug", "author_desc",
        "author_facebook_url", "author_x_url", "author_in_url",
        "author_status", "author_banner", "author_banner_mobile", "author_avatar",
      ]
      for (const key of supported) {
        if (Object.prototype.hasOwnProperty.call(command, key) && command[key] !== undefined) {
          fd.append(key, command[key])
        }
      }
      if (Object.prototype.hasOwnProperty.call(command, "language") && command.language !== undefined) {
        fd.append("language", command.language)
      }
      const res = await this.$axios.put(`${this.endpoint}/admin/${id}`, fd)
      const dto = res.data && res.data.data
      const entity = dto ? mapAuthor(dto) : null
      const safeEntity = entity && !entity._languageError ? entity : null
      return { data: safeEntity, message: "", err: null }
    } catch (err) {
      return { data: null, message: "", err: normalizeError(err) }
    }
  }
}

export default CorporateAuthorRepository
